/**
 * Screen 14's calls. Its own client, as screen 12's is: the shared one has
 * no DELETE, and the upload goes to S3 rather than the API.
 */

import type { Book } from "@baros/core";
import { request as shared } from "../api.js";


export class BooksApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "BooksApiError";
  }
}

async function request<T>(
  token: string,
  method: "GET" | "POST" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body, (status, message) => new BooksApiError(status, message));
}

const id = encodeURIComponent;

export interface Shelf {
  readonly books: readonly Book[];
  readonly totalBytes: number;
  /** What the org is charged for a month of the shelf; it counts against the AI cap. */
  readonly storageCentsPerMonth: number;
  /** The rate behind it, cents per GB-month. */
  readonly storageCentsPerGbMonth: number;
}

export interface NewBookBody {
  readonly title: string;
  readonly filename: string;
  readonly contentType: string;
  readonly sizeBytes: number;
}

/**
 * PUT the file to its presigned URL. XHR rather than fetch for the progress
 * events: a scanned cookbook can be 150 MB, and a bar's wifi is not quick.
 */
function putFile(url: string, file: File, contentType: string, onProgress: (fraction: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    // Must match the type the URL was signed for, or S3 refuses the signature.
    xhr.setRequestHeader("content-type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new BooksApiError(xhr.status, `The upload was refused (${xhr.status})`));
    xhr.onerror = () => reject(new BooksApiError(0, "The upload did not reach storage — check the connection and try again"));
    xhr.send(file);
  });
}

export const booksApi = {
  shelf: (token: string) => request<Shelf>(token, "GET", "/v1/org/books"),

  /** Create the row, upload the file, then queue it for indexing. */
  async add(token: string, file: File, title: string, onProgress: (fraction: number) => void): Promise<Book | null> {
    const body: NewBookBody = { title, filename: file.name, contentType: file.type, sizeBytes: file.size };
    const created = await request<{ book: Book; url: string }>(token, "POST", "/v1/org/books", body);
    await putFile(created.url, file, created.book.contentType, onProgress);
    const synced = await request<{ book: Book | null }>(token, "POST", `/v1/org/books/${id(created.book.bookId)}/sync`);
    return synced.book;
  },

  /** Retry indexing a book that failed. */
  resync: (token: string, bookId: string) =>
    request<{ book: Book | null }>(token, "POST", `/v1/org/books/${id(bookId)}/sync`),

  remove: (token: string, bookId: string) =>
    request<{ removed: boolean }>(token, "DELETE", `/v1/org/books/${id(bookId)}`),
};
